import { Flex, Icon, Text, Spinner, useColorModeValue } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { MdCheckCircle, MdHelpOutline } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import { typography, useSemanticColor } from 'theme/tokens';
import { READ_FAILED_I18N_KEY, scopePromptI18nKey } from '../../_shared/readState';
import { ChecklistItemId } from '../constants';
import { ChecklistUnknown } from '../hooks/useChecklistItems';

type Props = {
  id: ChecklistItemId;
  icon: IconType;
  route: string;
  completed: boolean;
  loading: boolean;
  /**
   * Set when the read behind this row could not answer (R90-2). The row then
   * states that instead of the "Go →" call to action, which is the affordance
   * that carried the false claim: an operator who HAS courts was being told to
   * go and create some.
   */
  unknown?: ChecklistUnknown | null;
  onNavigate?: () => void;
};

export default function ChecklistItem({ id, icon, route, completed, loading, unknown, onNavigate }: Props) {
  const navigate = useNavigate();
  const hoverBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const defaultIcon = useColorModeValue('secondaryGray.400', 'secondaryGray.600');
  const completedIcon = useColorModeValue('green.500', 'green.400');
  const textColor = useColorModeValue(
    completed ? 'secondaryGray.500' : 'secondaryGray.900',
    completed ? 'gray.500' : 'white',
  );
  const linkColor = useColorModeValue('brand.500', 'brand.400');
  const mutedColor = useSemanticColor('textSecondary');
  const isUnknown = !loading && !completed && !!unknown;

  const handleClick = () => {
    onNavigate?.();
    navigate(route);
  };

  return (
    <Flex
      as="li"
      role="listitem"
      align="center"
      px={6}
      py={3}
      minH="44px"
      cursor="pointer"
      _hover={{ bg: hoverBg }}
      transition="background 0.15s"
      onClick={handleClick}
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      _focusVisible={{ ring: 2, ringColor: 'brand.500' }}
      borderRadius="md"
    >
      {loading ? (
        <Spinner size="sm" mr={3} />
      ) : completed ? (
        <Icon as={MdCheckCircle} w="20px" h="20px" color={completedIcon} mr={3} />
      ) : isUnknown ? (
        // Deliberately NOT the item's own icon: that icon beside no caption is
        // exactly what an unticked row looks like, and the point of this arm is
        // that the row is not making that claim.
        <Icon as={MdHelpOutline} w="20px" h="20px" color={defaultIcon} mr={3} />
      ) : (
        <Icon as={icon} w="20px" h="20px" color={defaultIcon} mr={3} />
      )}

      <Text color={textColor} {...typography.body} flex={1}>
        {translate(`walkthrough.${id}` as any)}
      </Text>

      {completed ? (
        <Text {...typography.caption} color="green.500">
          {translate('walkthrough.done')}
        </Text>
      ) : isUnknown ? (
        // Muted, not `linkColor`: this is a statement, not a call to action.
        // Both strings are BORROWED existing keys — `src/i18n/{en,fr}.ts` are
        // frozen inside open PR #577 (R96-7) — and the branch that picks the
        // scope key lives in `readState.ts`, never copied here (D59).
        <Text {...typography.caption} color={mutedColor} textAlign="right">
          {translate(
            (unknown!.reason === 'unreadable'
              ? READ_FAILED_I18N_KEY
              : scopePromptI18nKey(unknown!.gate)) as any,
          )}
        </Text>
      ) : (
        <Text {...typography.caption} color={linkColor} fontWeight="600">
          {translate('walkthrough.go')} &rarr;
        </Text>
      )}
    </Flex>
  );
}
